import { Box, Link, Text, VStack } from "@chakra-ui/react";
import { RiBuildingLine } from "@remixicon/react";

import EmptyState, { EmptyStateProps } from "@/common/components/EmptyState";
import { IcoSegment } from "@/common/components/Icons";
import { useSegmentRouteParam } from "@/company/hooks/useSegmentRouteParam";
import { UIFilter } from "@reflag/shared/filter";
import { SegmentDTO } from "@reflag/shared/segmentAPI";

type Props = EmptyStateProps & {
  segment: SegmentDTO;
  filter: UIFilter;
  isError?: boolean;
  onRetry?: () => void;
};

export default function CompaniesEmptyState({
  segment,
  filter,
  isLoading,
  isError,
  onRetry,
  ...rest
}: Props) {
  const { setViewType } = useSegmentRouteParam();

  if (segment.isAllSegment && filter === undefined) {
    return (
      <EmptyState
        description="Companies represent groups of end-users. Companies will appear in Reflag when initializing our SDK or over the API."
        error="Failed to load companies"
        flexGrow={1}
        icon={<RiBuildingLine size="48px" />}
        isError={isError}
        isLoading={isLoading}
        title="No companies found"
        onRetry={onRetry}
        {...rest}
      />
    );
  }

  if (
    filter.type === "group" &&
    filter.filters?.some((item) =>
      ["userAttribute", "userIds"].includes(item.type),
    )
  ) {
    return (
      <EmptyState
        description={
          <VStack gap={2}>
            <Box>
              Your filters include{" "}
              <Text as="span" fontWeight="bold">
                user specific filters
              </Text>
              .
            </Box>
            <Box>
              See{" "}
              <Link onClick={() => setViewType("users")}>matching users</Link>{" "}
              instead?
            </Box>
          </VStack>
        }
        error="Failed to load companies"
        flexGrow={1}
        icon={<IcoSegment boxSize="48px" />}
        title="Current filters only match users"
      />
    );
  }

  return (
    <EmptyState
      description="Try adjusting the filters. Tweak the table columns to show only what you need and save them for later!"
      error="Failed to load companies"
      flexGrow={1}
      icon={<IcoSegment boxSize="48px" />}
      isError={isError}
      isLoading={isLoading}
      title="No companies found"
      onRetry={onRetry}
      {...rest}
    />
  );
}
